import React, { useEffect, useState } from 'react'
import { toJS } from 'mobx'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, CircularProgress } from '@material-ui/core'
import { AddCircle, Close as CloseIcon } from '@material-ui/icons'
import { Formik, Form, FieldArray } from 'formik'
import { observer } from '~/common/mobx.decorator'
import { resumeParseStore } from './resume-parse.store'
import useYupValidation from '~/common/hooks/use-yup-validation.hook'
import { handleDatePickerModalClick } from '~/common/helpers/focus.helper'
import { CandidateSection, EducationItem, WorkItem } from './components'
import {
	StyledDialogHeader,
	StyledScrollableContent,
	StyledDialogFooter,
	StyledSection,
	StyledSectionTitle,
	StyledAddButton,
	AddButtonLabel,
	StyledBottomSpacer,
} from './resume-parse-dialog.style'
import { Typography } from '~/components/typography'
import { appStore } from '~/stores'
import { NotifyDialog } from '~/components/notify'
import { useTranslation } from '@opus/web.core.hooks.use-translation'

const ResumeParseDialog = observer(({ open, onClose, workerId, onSubmit }) => {
	const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
	const { data: storeData, loading, reset, error, isSubmitting: isStoreSubmitting } = resumeParseStore
	const { RESUME_PARSE_SCHEMA } = useYupValidation()
	const { t } = useTranslation()

	useEffect(() => {
		if (open && workerId) {
			resumeParseStore.loadIntervalResumeParser(workerId)
		}
	}, [open, workerId])

	useEffect(() => {
		return () => {
			resumeParseStore.clearLoadIntervalResumeParser()
		}
	}, [])

	const handleSubmit = async (values) => {
		try {
			const result = await resumeParseStore.submitResumeParseData(values)
			if (result.success) {
				// Show success message
				appStore.notifyStore.success('Resume parse data saved successfully!')
				if (onSubmit) {
					onSubmit(values)
					reset()
				}
			}
		} catch (error) {
			// Error is already handled in the store
			appStore.notifyStore.error(error.message || 'Failed to save resume parse data')
		}
	}

	const handleAddEducation = (arrayHelpers) => {
		const newEducation = {
			id: null,
			school: '',
			city: '',
			state: '',
			country: '',
			startDate: '',
			endDate: '',
			graduationDate: '',
			degreeLevel: '',
			areaOfStudy: '',
			gpa: null,
		}
		arrayHelpers.push(newEducation)
	}

	const handleAddWork = (arrayHelpers) => {
		const newWork = {
			id: null,
			company: '',
			position: '',
			startDate: '',
			endDate: '',
			address1: '',
			address2: '',
			city: '',
			state: '',
			country: '',
			zipcode: '',
		}
		arrayHelpers.push(newWork)
	}

	const handleDeleteEducation = (index, arrayHelpers) => {
		const item = arrayHelpers.form.values['education'][index]
		if (item && item.id) {
			arrayHelpers.replace(index, { ...item, _destroy: true })
		} else {
			arrayHelpers.remove(index)
		}
	}

	const handleDeleteWork = (index, arrayHelpers) => {
		const item = arrayHelpers.form.values['workHistory'][index]
		if (item && item.id) {
			arrayHelpers.replace(index, { ...item, _destroy: true })
		} else {
			arrayHelpers.remove(index)
		}
	}

	const handleCancelClick = () => {
		setCancelConfirmOpen(true)
	}

	const handleConfirmCancel = () => {
		setCancelConfirmOpen(false)
		reset()
		if (onClose) onClose()
	}

	const handleDismissCancel = () => {
		setCancelConfirmOpen(false)
	}

	if (error) {
		return (
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<DialogTitle>
					Resume Parse
					<IconButton style={{ position: 'absolute', right: 8, top: 8 }} onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Typography color="error">Error loading resume data: {error}</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Close</Button>
				</DialogActions>
			</Dialog>
		)
	}

	return (
		<Dialog
			open={open}
			disableEnforceFocus
			disableBackdropClick
			disableEscapeKeyDown
			onClose={onClose}
			maxWidth={false}
			PaperProps={{
				style: {
					width: 600,
					borderRadius: 16,
					padding: 0,
					display: 'flex',
					flexDirection: 'column',
					height: '90vh',
					maxHeight: '90vh',
					overflow: 'hidden',
					position: 'relative',
				},
			}}
		>
			<StyledDialogHeader>
				<Typography variant="h5" color="primary.main">
					Resume parser review
				</Typography>
				{!loading && (
					<Typography variant="body1" style={{ color: appStore.getTheme.colors.content.veryDark }}>
						Your resume has been successfully uploaded and parsed. Please review the extracted information below before confirming.
					</Typography>
				)}
			</StyledDialogHeader>

			{loading ? (
				<StyledScrollableContent style={{ textAlign: 'center', justifyContent: 'center', flexDirection: 'row', alignItems: 'center' }}>
					<CircularProgress />
					<Typography variant="body1">Loading resume data...</Typography>
				</StyledScrollableContent>
			) : (
				<Formik
					initialValues={{
						candidate: (storeData && toJS(storeData.candidate)) || {},
						education: (storeData && toJS(storeData.education)) || [],
						workHistory: (storeData && toJS(storeData.workHistory)) || [],
					}}
					validationSchema={RESUME_PARSE_SCHEMA}
					onSubmit={handleSubmit}
					validateOnChange={true}
					validateOnBlur={true}
					enableReinitialize
				>
					{({ values, isSubmitting }) => (
						<Form style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }} onClick={handleDatePickerModalClick}>
							<StyledScrollableContent>
								<CandidateSection />
								{/* Education Section */}
								<StyledSection>
									<FieldArray name="education">
										{(arrayHelpers) => (
											<>
												{Array.isArray(values.education) && values.education?.length > 0 && (
													<>
														{values.education
															.filter((education) => !education._destroy) // Only show items that are not marked for deletion
															.map((education) => {
																// Find the original index in the full array
																const actualIndex = values.education.findIndex((item) => item === education)
																return (
																	<>
																		<StyledSectionTitle>{t('Education')}</StyledSectionTitle>
																		<EducationItem
																			key={actualIndex}
																			education={education}
																			index={actualIndex}
																			onDelete={() => handleDeleteEducation(actualIndex, arrayHelpers)}
																		/>
																	</>
																)
															})}
													</>
												)}
												<StyledAddButton type="button" onClick={() => handleAddEducation(arrayHelpers)}>
													<AddCircle htmlColor={appStore.getTheme.colors.hyperLink.medium} />
													<AddButtonLabel variant="title2">Add Education</AddButtonLabel>
												</StyledAddButton>
											</>
										)}
									</FieldArray>
								</StyledSection>

								{/* Work History Section */}
								<StyledSection>
									<FieldArray name="workHistory">
										{(arrayHelpers) => (
											<>
												{Array.isArray(values.workHistory) && values.workHistory?.length > 0 && (
													<>
														{values.workHistory
															.filter((work) => !work._destroy) // Only show items that are not marked for deletion
															.map((work) => {
																// Find the original index in the full array
																const actualIndex = values.workHistory.findIndex((item) => item === work)
																return (
																	<>
																		<StyledSectionTitle>Work History</StyledSectionTitle>
																		<WorkItem key={actualIndex} work={work} index={actualIndex} onDelete={() => handleDeleteWork(actualIndex, arrayHelpers)} />
																	</>
																)
															})}
													</>
												)}
												<StyledAddButton type="button" onClick={() => handleAddWork(arrayHelpers)}>
													<AddCircle htmlColor={appStore.getTheme.colors.hyperLink.medium} />
													<AddButtonLabel variant="title2">Add Work History</AddButtonLabel>
												</StyledAddButton>
											</>
										)}
									</FieldArray>
								</StyledSection>

								<StyledBottomSpacer />
							</StyledScrollableContent>

							<StyledDialogFooter>
								<Button variant="outlined" onClick={handleCancelClick} style={{ flex: 1, height: 48, marginRight: 8 }}>
									CANCEL
								</Button>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									disabled={isSubmitting || isStoreSubmitting}
									style={{ flex: 1, height: 48, marginLeft: 8 }}
								>
									{isStoreSubmitting ? 'SAVING...' : 'CONFIRM'}
								</Button>
							</StyledDialogFooter>
							{/* Delete confirmation removed: delete actions execute immediately */}
							<NotifyDialog
								disableEnforceFocus
								open={cancelConfirmOpen}
								okText="CONFIRM"
								onOk={handleConfirmCancel}
								title={t('Leave this page?')}
								onCancel={handleDismissCancel}
							>
								<Typography>{t("If you cancel now, you'll lose the parsed data. Do you want to continue?")}</Typography>
							</NotifyDialog>
						</Form>
					)}
				</Formik>
			)}
		</Dialog>
	)
})

export default ResumeParseDialog
